import { useEffect, useRef, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useConversation } from '@/hooks/useConversation'
import type { ChatMessage } from '@/hooks/useConversation'
import { useSendMessage } from '@/hooks/useSendMessage'
import { useMarkRead } from '@/hooks/useMarkRead'
import { useBlockUser } from '@/hooks/useBlockUser'
import { useUnblockUser } from '@/hooks/useUnblockUser'
import { MessageBubble } from '@/components/chat/MessageBubble'
import { MessageInput } from '@/components/chat/MessageInput'
import { BlockedUserBanner } from '@/components/chat/BlockedUserBanner'
import { ConversationMenu } from '@/components/chat/ConversationMenu'
import { Button } from '@/components/ui/button'
import { toast } from '@/lib/toast'

export function ConversationPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: conv, isLoading, refetch } = useConversation(id)
  const sendMessage = useSendMessage()
  const markRead = useMarkRead()
  const blockUser = useBlockUser()
  const unblockUser = useUnblockUser()

  const scrollRef = useRef<HTMLDivElement>(null)
  const lastMessageId = useRef<string | null>(null)
  const [showBlockConfirm, setShowBlockConfirm] = useState(false)
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false)

  // Mark messages read on mount and when new messages arrive
  useEffect(() => {
    if (id) markRead.mutate(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, conv?.messages?.length])

  // Auto-scroll to bottom when new messages appear
  useEffect(() => {
    if (!conv?.messages.length || !scrollRef.current) return
    const newest = conv.messages[conv.messages.length - 1]
    if (lastMessageId.current !== newest.id) {
      const isInitialLoad = lastMessageId.current === null
      lastMessageId.current = newest.id
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: isInitialLoad ? 'auto' : 'smooth',
        })
      })
    }
  }, [conv?.messages])

  const displayName = useMemo(() => {
    if (!conv) return ''
    const fn = conv.other_user_first_name
    const ln = conv.other_user_last_name
    if (fn && fn !== '(Pending)' && ln && ln !== '(Pending)') {
      return `${fn} ${ln}`
    }
    return `@${conv.other_user_username}`
  }, [conv])

  async function handleSend(body: string) {
    if (!id) return
    try {
      await sendMessage.mutateAsync({ conversationId: id, body })
      await refetch()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to send message'
      )
    }
  }

  async function handleBlock() {
    if (!conv) return
    try {
      await blockUser.mutateAsync({ targetUserId: conv.other_user_id })
      toast.success('User blocked')
      navigate('/chat', { replace: true })
    } catch {
      toast.error('Failed to block user')
    }
  }

  async function handleUnblock() {
    if (!conv) return
    try {
      await unblockUser.mutateAsync(conv.other_user_id)
      toast.success('User unblocked')
      await refetch()
    } catch {
      toast.error('Failed to unblock user')
    }
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (!conv) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-background px-6 text-center gap-4">
        <p className="text-muted-foreground">Conversation not found.</p>
        <Button onClick={() => navigate('/chat')}>Back to Chat</Button>
      </div>
    )
  }

  const messagesWithSeparators = buildMessageList(conv.messages)

  return (
    <div className="fixed inset-0 flex flex-col bg-background z-50">
      {/* Header — fixed top */}
      <header className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b bg-background">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/chat')}
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold truncate">{displayName}</h1>
          {conv.other_user_first_name === '(Pending)' && (
            <p className="text-xs text-muted-foreground">Profile incomplete</p>
          )}
        </div>
        <ConversationMenu
          isBlocked={conv.is_blocked_by_me}
          onBlock={() => setShowBlockConfirm(true)}
          onUnblock={() => setShowUnblockConfirm(true)}
        />
      </header>

      {/* Messages — flex-1, scrollable */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3">
        {conv.is_blocked_by_me ? (
          <BlockedUserBanner onUnblock={() => setShowUnblockConfirm(true)} />
        ) : messagesWithSeparators.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-sm text-muted-foreground text-center">
              No messages yet. Say hi! 👋
            </p>
          </div>
        ) : (
          messagesWithSeparators.map((item) =>
            item.type === 'separator' ? (
              <div key={item.key} className="text-center my-3">
                <span className="text-xs text-muted-foreground bg-muted/50 rounded-full px-3 py-1">
                  {item.label}
                </span>
              </div>
            ) : (
              <MessageBubble
                key={item.message.id}
                message={item.message}
                showDate={false}
              />
            )
          )
        )}
      </div>

      {/* Input — fixed bottom */}
      {!conv.is_blocked_by_me && (
        <div className="flex-shrink-0">
          <MessageInput onSend={handleSend} disabled={false} />
        </div>
      )}

      {/* Block confirmation dialog */}
      {showBlockConfirm && (
        <ConfirmDialog
          title="Block this user?"
          description="You won't see messages from them anymore."
          confirmLabel="Block"
          destructive
          onConfirm={() => {
            setShowBlockConfirm(false)
            handleBlock()
          }}
          onCancel={() => setShowBlockConfirm(false)}
        />
      )}

      {/* Unblock confirmation dialog */}
      {showUnblockConfirm && (
        <ConfirmDialog
          title="Unblock this user?"
          description="You'll be able to send and receive messages again."
          confirmLabel="Unblock"
          onConfirm={() => {
            setShowUnblockConfirm(false)
            handleUnblock()
          }}
          onCancel={() => setShowUnblockConfirm(false)}
        />
      )}
    </div>
  )
}

// --- Helpers ---

type ListItem =
  | { type: 'separator'; key: string; label: string }
  | { type: 'message'; key: string; message: ChatMessage }

function buildMessageList(messages: ChatMessage[]): ListItem[] {
  const result: ListItem[] = []
  let lastDate: string | null = null

  for (const m of messages) {
    const date = new Date(m.created_at)
    const dateKey = date.toDateString()
    if (dateKey !== lastDate) {
      result.push({
        type: 'separator',
        key: `sep-${dateKey}`,
        label: formatDateSeparator(date),
      })
      lastDate = dateKey
    }
    result.push({ type: 'message', key: m.id, message: m })
  }

  return result
}

function formatDateSeparator(d: Date): string {
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)

  if (d.toDateString() === today.toDateString()) return 'Today'
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  })
}

function ConfirmDialog({
  title,
  description,
  confirmLabel,
  destructive,
  onConfirm,
  onCancel,
}: {
  title: string
  description: string
  confirmLabel: string
  destructive?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg shadow-lg p-6 mx-4 max-w-sm w-full">
        <h3 className="font-semibold text-base mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground mb-4">{description}</p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-md border hover:bg-accent"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm rounded-md text-white ${
              destructive
                ? 'bg-destructive hover:bg-destructive/90'
                : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
